import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import DocumentUpload from '../Common/DocumentUpload';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import { useAuth } from '../../Context/AuthContext';
import { getAllProviderSettings } from '../../Services/ProviderService';
import CustomDatePicker from '../Common/CustomDatePicker';

const SETTING_TYPE_IDS = {
  branch: 33,
  discharge_type: 34,
  rank: 35,
  role: 36,
};

const mapProviderSettingItem = (item) => ({
  value: item.id ?? item.Id ?? item.value ?? item.value_id ?? null,
  label: item.name ?? item.Name ?? item.code ?? String(item.id ?? ''),
});

const validationSchema = Yup.object({
  branch: Yup.number().nullable(),
  rank: Yup.number().nullable(),
  role: Yup.number().nullable(),
  service: Yup.string().trim().nullable(),
  is_currently_serving: Yup.boolean().nullable(),
  start_date: Yup.date().nullable().typeError('Invalid date format'),
  end_date: Yup.date().nullable().typeError('Invalid date format'),
  discharge_type: Yup.number().nullable(),
  last_military_service_location: Yup.string().trim().nullable(),
  document: Yup.mixed().nullable(),
});

const emptyForm = {
  id: null,
  provider_id: null,
  organization_id: null,
  status_id: null,
  file_id: null,
  branch: null,
  rank: null,
  role: null,
  service: '',
  is_currently_serving: null,
  start_date: null,
  end_date: null,
  discharge_type: null,
  last_military_service_location: '',
  document: null,
  type: null,
};

export default function MilitaryExperienceModal({
  isOpen,
  title,
  initialData,
  onClose,
  onSave,
  isLoading,
}) {
  const { token } = useAuth();
  const [providerSettings, setProviderSettings] = useState({
    branch: [],
    discharge_type: [],
    rank: [],
    role: [],
  });
  const [settingsLoading, setSettingsLoading] = useState(false);

  const loadProviderSettings = async () => {
    if (!token) return;
    setSettingsLoading(true);

    try {
      const entries = Object.entries(SETTING_TYPE_IDS);
      const results = await Promise.all(
        entries.map(async ([key, typeId]) => {
          try {
            const response = await getAllProviderSettings(token, typeId);
            if (response?.is_success) {
              return {
                key,
                options: Array.isArray(response.data) ? response.data.map(mapProviderSettingItem) : [],
              };
            }else if (response?.is_success === false && response?.status_code === 500) {
              toast.error('Internal server error. Please contact support team.');
            } else {
              toast.error(response?.message || `Failed to load settings`);
              console.error(`Failed to load ${key} settings:`, response?.message || 'Unknown error');
            }
          } catch (error) {
            console.error(`Failed to load ${key} settings:`, error);
          }
          return { key, options: [] };
        })
      );

      setProviderSettings(results.reduce((acc, item) => ({ ...acc, [item.key]: item.options }), {}));
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadProviderSettings();
  }, [token]);

  const initialValues = useMemo(() => {
    if (!initialData) return emptyForm;

    return {
      ...emptyForm,
      ...initialData,
      document: initialData.document || initialData.file_name || initialData.fileName || null,
      branch: initialData.branch ?? null,
      rank: initialData.rank ?? null,
      role: initialData.role ?? null,
      discharge_type: initialData.discharge_type ?? null,
      is_currently_serving: initialData.is_currently_serving ?? null,
      service: initialData.service ?? '',
      start_date: initialData.start_date ?? '',
      end_date: initialData.end_date ?? '',
      last_military_service_location: initialData.last_military_service_location ?? '',
    };
  }, [initialData]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        ...values,
        id: values.id || null,
        branch: values.branch !== '' && values.branch !== null && values.branch !== undefined ? Number(values.branch) : null,
        rank: values.rank !== '' && values.rank !== null && values.rank !== undefined ? Number(values.rank) : null,
        role: values.role !== '' && values.role !== null && values.role !== undefined ? Number(values.role) : null,
        discharge_type: values.discharge_type !== '' && values.discharge_type !== null && values.discharge_type !== undefined ? Number(values.discharge_type) : null,
        is_currently_serving: values.is_currently_serving === true || values.is_currently_serving === 'true' ? true : values.is_currently_serving === false || values.is_currently_serving === 'false' ? false : null,
        start_date: values.start_date || null,
        end_date: values.end_date || null,
        type: values.id ? 'update' : 'create',
      };
      onSave(payload);
    },
  });

  const handleSelectChange = (fieldName) => (value) => {
    const parsedValue = value === '' || value === null ? null : Number(value);
    formik.setFieldValue(fieldName, parsedValue);
  };

  const handleCheckboxChange = (event) => {
    formik.setFieldValue(event.target.name, event.target.checked);
  };

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(var(--hc-black-rgb), 0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content border-0 rounded-4">
          <div className="modal-header border-bottom-0 px-4 py-2 d-flex justify-content-between align-items-center">
            <h5 className="modal-title fw-bold hc-text-primary m-0">{title}</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isLoading} />
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className="modal-body px-4 py-2">
              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-semibold hc-text-primary mb-3">Military Experience</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <SearchableDropdown
                      label="Branch"
                      placeholder="Select Option"
                      options={providerSettings.branch}
                      value={formik.values.branch}
                      onChange={handleSelectChange('branch')}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      emptyMessage="No branches found"
                    />
                  </div>
                  <div className="col-md-6">
                    <SearchableDropdown
                      label="Rank"
                      placeholder="Select Option"
                      options={providerSettings.rank}
                      value={formik.values.rank}
                      onChange={handleSelectChange('rank')}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      emptyMessage="No ranks found"
                    />
                  </div>
                  <div className="col-md-6">
                    <SearchableDropdown
                      label="Role"
                      placeholder="Select Option"
                      options={providerSettings.role}
                      value={formik.values.role}
                      onChange={handleSelectChange('role')}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      emptyMessage="No roles found"
                    />
                  </div>
                  <div className="col-md-6">
                    <SearchableDropdown
                      label="Discharge Type"
                      placeholder="Select Option"
                      options={providerSettings.discharge_type}
                      value={formik.values.discharge_type}
                      onChange={handleSelectChange('discharge_type')}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      emptyMessage="No discharge types found"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Service</label>
                    <input
                      {...formik.getFieldProps('service')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter service"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Currently Serving</label>
                    <select
                      name="is_currently_serving"
                      value={formik.values.is_currently_serving === true ? 'true' : formik.values.is_currently_serving === false ? 'false' : ''}
                      onChange={(event) => formik.setFieldValue('is_currently_serving', event.target.value === 'true')}
                      className="form-select rounded-3 shadow-sm"
                    >
                      <option value="">Select Option</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Start Date</label>
                    <CustomDatePicker
                    name="start_date"
                    formik={formik}
                    />
                    {/* <input
                      type="date"
                      {...formik.getFieldProps('start_date')}
                      className="form-control rounded-3 shadow-sm"
                    /> */}
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">End Date</label>
                    <CustomDatePicker
                    name="end_date"
                    formik={formik}
                    />
                    {/* <input
                      type="date"
                      {...formik.getFieldProps('end_date')}
                      className="form-control rounded-3 shadow-sm"
                    /> */}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Last Military Service Location</label>
                    <input
                      {...formik.getFieldProps('last_military_service_location')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter last service location"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Document</h6>
                <DocumentUpload
                  id="military-document-input"
                  label="Upload Document"
                  value={formik.values.document}
                  onChange={(file) => formik.setFieldValue('document', file)}
                />
              </div>
            </div>

            <div className="modal-footer border-top-0 p-4 d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-light rounded-3 px-4" onClick={onClose} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn hc-bg-primary text-white rounded-3 px-4 d-flex align-items-center justify-content-center gap-2" disabled={isLoading} aria-busy={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
